import type { FeatureFlag } from '@swf/core/enums';
import { Header } from '@swf/core/enums';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { BoundRequest, BoundRequestOptions } from '@swf/core/types/Request';
import { renderer } from '@swf/core/utils';

type SportsRequestProps = Omit<BoundRequestOptions, 'service'> & {
  bypassBffCache?: boolean;
  includeFeatures?: FeatureFlag[];
  path: `/${string}`;
};

const buildSportsRequest =
  (request: BoundRequest, rootState: RootState) =>
  <T>(requestOptions: SportsRequestProps) => {
    const {
      bypassBffCache = renderer === 'server',
      headers,
      includeFeatures = [],
      path,
      ...restOptions
    } = requestOptions;
    const accountCountry = select.account.getCountry(rootState);
    const isEligibleForPromotionalContent =
      select.sports.isEligibleForPromotionalContent(rootState);
    const enabledFeatureFlags = includeFeatures.filter((flag) =>
      select.feature.check(rootState, flag)
    );

    const sportsHeaders = {
      // Disable caching for requests from vader
      ...(bypassBffCache && { 'Cache-Control': 'max-age=0' }),
      // Pass account country for api gateway authorizer (content filtering)
      ...(accountCountry && { [Header.IspAccountCountry]: accountCountry }),
      ...(enabledFeatureFlags.length && { [Header.IspFeatures]: enabledFeatureFlags.join() }),
      ...(isEligibleForPromotionalContent && {
        [Header.IspPromotionsPermitted]: 'true'
      }),
      ...headers
    };

    return request<T>({
      ...restOptions,
      headers: sportsHeaders,
      http: true,
      service: `/sports/web${path}`
    });
  };

export default buildSportsRequest;
